/**
 * sentry.js — Sentry integration via crashLogger.setReporter().
 *
 * Only activates when REACT_APP_SENTRY_DSN is set AND in production.
 * In development, crashLogger already prints to the console — no Sentry noise.
 *
 * Wires up:
 *   - Sentry.init() with React integration and route instrumentation
 *   - crashLogger.setReporter() so all crash/error/warn/info events forward to Sentry
 *   - React ErrorBoundary integration (handled automatically via Sentry.init)
 *
 * Payload sanitization:
 *   - User email and name are stripped from events (GDPR / privacy)
 *   - district_id is included for triage but not linked to PII
 *   - Stack traces are capped at 2000 chars (already enforced in crash-logger.js)
 *
 * Usage:
 *   import { initSentry } from './lib/sentry';
 *   initSentry(); // call once before crashLogger.init()
 */

import * as Sentry from '@sentry/react';
import { crashLogger } from './crash-logger';

const DSN = process.env.REACT_APP_SENTRY_DSN;
const isProd = process.env.NODE_ENV === 'production';

/**
 * Set Sentry user context after authentication.
 * Only stores a non-PII user ID and the district ID — no emails, names, or pastoral data.
 * Safe to call in both prod and dev (no-op when Sentry is not initialized).
 */
export function setSentryUser(userId, districtId) {
  if (!DSN || !isProd) return;
  Sentry.setUser(userId ? { id: userId } : null);
  Sentry.setContext('district', districtId ? { id: districtId } : null);
}

// Sentry level map: crashLogger levels → Sentry severity
const LEVEL_MAP = {
  error: 'error',
  warn:  'warning',
  info:  'info',
};

export function initSentry() {
  if (!DSN || !isProd) return;

  Sentry.init({
    dsn: DSN,
    environment: process.env.REACT_APP_ENV || 'production',
    release: process.env.REACT_APP_VERSION || 'keves@unknown',

    // 10% of sessions captured for performance tracing — adjust for traffic volume
    tracesSampleRate: 0.1,

    // Don't send source maps to Sentry since GENERATE_SOURCEMAP=false in production
    // (source maps are not generated — set to false to avoid misleading frame hints)
    attachStacktrace: true,

    // Sanitize outgoing events: remove PII fields
    beforeSend(event) {
      // Strip user identity from breadcrumbs / request context
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
        delete event.user.ip_address;
      }
      // Remove any headers that may carry auth tokens
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['Cookie'];
      }
      return event;
    },

    integrations: [
      Sentry.browserTracingIntegration(),
    ],
  });

  // Wire crashLogger → Sentry
  crashLogger.setReporter((level, message, extra) => {
    const sentryLevel = LEVEL_MAP[level] || 'info';

    if (message === 'react_crash') {
      // React render crashes — already caught by ErrorBoundary.componentDidCatch
      // Sentry's React integration captures these via its own ErrorBoundary wrapper.
      // We re-emit here for completeness with our structured extra data.
      Sentry.captureException(new Error(extra?.message || 'react_crash'), {
        level: sentryLevel,
        extra,
        tags: { type: 'react_crash' },
      });
    } else if (level === 'error') {
      Sentry.captureException(new Error(String(message)), {
        level: sentryLevel,
        extra,
        tags: { type: message },
      });
    } else {
      // warn / info — capture as breadcrumb, not a full event (avoids quota burn)
      Sentry.addBreadcrumb({
        category: 'keves',
        message: String(message),
        level: sentryLevel,
        data: extra,
      });
    }
  });
}
